import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Users, User, MapPin } from 'lucide-react';
import type { Session, Place, Business } from '@/types';
import { useSocket } from '@/hooks/useSocket';
import { QrDisplay } from '@/components/QrDisplay';
import { Toaster, toast } from 'sonner';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

export function PlacePage() {
  const { placeId } = useParams<{ placeId: string }>();

  const [step, setStep] = useState<'form' | 'created'>('form');
  const [form, setForm] = useState({ name: '', party_size: 1 });
  const [session, setSession] = useState<Session | null>(null);
  const [place, setPlace] = useState<Place | null>(null);
  const [business, setBusiness] = useState<Business | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { connected, lastEvent, joinPlace } = useSocket();

  const fetchPlace = useCallback(async () => {
    if (!placeId) return;
    try {
      const res = await fetch(`${API_BASE}/places/${placeId}`);
      if (res.ok) {
        const data = (await res.json()) as { place: Place; business: Business };
        setPlace(data.place);
        setBusiness(data.business);
      }
    } catch {
      // ignore
    }
  }, [placeId]);

  useEffect(() => {
    fetchPlace();
  }, [fetchPlace]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!placeId) return;
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/places/${placeId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_name: form.name,
          party_size: form.party_size,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al unirse a la cola');
      }

      const data = (await res.json()) as Session;
      setSession(data);
      setStep('created');
      toast.success('¡Te has unido a la cola!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const fetchSession = useCallback(async () => {
    if (!session?.id) return;
    try {
      const res = await fetch(`${API_BASE}/sessions/${session.id}`);
      if (res.ok) {
        const data = (await res.json()) as Session;
        setSession(data);
      }
    } catch {
      // ignore
    }
  }, [session?.id]);

  useEffect(() => {
    if (connected && placeId && step === 'created') {
      joinPlace(placeId);
    }
  }, [connected, placeId, step, joinPlace]);

  useEffect(() => {
    if (!lastEvent) return;
    if (lastEvent.event === 'session:updated' || lastEvent.event === 'queue:changed') {
      fetchSession();
    }
  }, [lastEvent, fetchSession]);

  if (step === 'created' && session) {
    return (
      <div className="flex flex-col items-center min-h-screen py-8 px-4 animate-fade-in" style={{ background: 'var(--qr-bg)' }}>
        <Toaster position="bottom-center" richColors />
        {business && (
          <div className="text-center mb-4">
            {business.logo_url && <img src={business.logo_url} alt={business.name} className="h-10 object-contain mx-auto mb-2" />}
            <h2 className="text-lg font-semibold" style={{ color: 'var(--qr-text-secondary)' }}>
              Estás en cola para: <span style={{ color: 'var(--qr-text)' }}>{place?.name || business.name}</span>
            </h2>
          </div>
        )}
        <div className="animate-qr-pulse rounded-2xl">
          <QrDisplay session={session} business={business} />
        </div>
        {session.status === 'waiting' && typeof session.position === 'number' && (
          <p className="mt-4 text-center text-lg animate-fade-in" style={{ color: 'var(--qr-text-secondary)' }}>
            Posición en cola:{' '}
            <strong style={{ color: 'var(--qr-primary)' }}>#{session.position + 1}</strong>
            {session.estimated_wait ? (
              <span className="block text-sm mt-1">
                ~{Math.round(session.estimated_wait / 60)} min estimados
              </span>
            ) : null}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen py-8 px-4 animate-fade-in" style={{ background: 'var(--qr-bg)' }}>
      <Toaster position="bottom-center" richColors />
      <div
        className="w-full max-w-sm p-6 rounded-2xl space-y-5"
        style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
      >
        <div className="text-center space-y-1">
          {business?.logo_url && <img src={business.logo_url} alt={business.name} className="h-10 object-contain mx-auto mb-2" />}
          <h1 className="text-2xl font-extrabold">{place?.name || 'Bienvenido'}</h1>
          {place && (
            <p className="text-sm flex items-center justify-center gap-1" style={{ color: 'var(--qr-text-secondary)' }}>
              <MapPin size={14} />
              {place.type === 'table' ? 'Mesa' : place.type === 'reception' ? 'Recepción' : place.type}
            </p>
          )}
          <p className="text-sm" style={{ color: 'var(--qr-text-secondary)' }}>
            Completa tus datos para unirte a la cola
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium flex items-center gap-1.5" style={{ color: 'var(--qr-text-secondary)' }}>
              <User size={14} /> Nombre
            </label>
            <input
              type="text"
              required
              minLength={1}
              placeholder="Tu nombre"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full px-4 py-3 rounded-xl outline-none"
              style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium flex items-center gap-1.5" style={{ color: 'var(--qr-text-secondary)' }}>
              <Users size={14} /> Comensales
            </label>
            <input
              type="number"
              required
              min={1}
              max={50}
              value={form.party_size}
              onChange={(e) => setForm((f) => ({ ...f, party_size: Number(e.target.value) }))}
              className="w-full px-4 py-3 rounded-xl outline-none"
              style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
            />
          </div>

          {error && <p className="text-sm" style={{ color: 'var(--qr-danger)' }}>{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
            style={{ background: 'var(--qr-primary)', color: '#fff' }}
          >
            {loading ? 'Uniendo...' : 'Unirse a la cola'}
          </button>
        </form>
      </div>
    </div>
  );
}
